import { isPlatformBrowser } from "@angular/common";
import { Component, HostBinding, Inject, PLATFORM_ID } from "@angular/core";
import {AuthContext} from "@eternalfest/core/auth/auth-context";
import { ActorType } from "@eternalfest/core/types/user/actor-type";
import {UserId} from "@eternaltwin/core/user/user-id";
import { $Boolean } from "kryo/boolean";
import { Observable } from "rxjs";
import { map as rxMap } from "rxjs/operators";

import {AuthService} from "../modules/auth/auth.service.mjs";
import { LocalStorageKey, LocalStorageService } from "../modules/storage/local-storage.service.mjs";

const DISABLE_SNOW_KEY: LocalStorageKey<boolean> = new LocalStorageKey("app/snow", $Boolean);
const PUBLIC_PROFILE_BANNER_KEY: LocalStorageKey<boolean> = new LocalStorageKey("app/public-profiles-seen", $Boolean);
const ITEMS_BY_FAMILY_BANNER_KEY: LocalStorageKey<boolean> = new LocalStorageKey("app/items-by-family-seen", $Boolean);
const CALENDAR_BANNER_KEY: LocalStorageKey<boolean> = new LocalStorageKey("app/calendar-seen", $Boolean);

@Component({
  selector: "ef-app",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  standalone: false,
})
export class AppComponent {
  public readonly localStorageService: LocalStorageService;
  public isAuthenticated$: Observable<boolean>;
  public userId$: Observable<UserId | undefined>;
  public userDisplayName$: Observable<string | undefined>;
  public title = "Eternalfest";
  public showCalendarBanner: boolean;
  public showPublicProfileBanner: boolean;
  public showItemsByFamilyBanner: boolean;

  @HostBinding("class.theme-goodbye")
  public isGoodbye: boolean = false;

  /**
   * Boolean indicating that it's the Christmas season.
   *
   * If true, this enables the Christmas theme with the Santa hat for Tuber
   * and snowfall in the background. It also enables the button to toggle the
   * snowfall animation.
   */
  @HostBinding("class.theme-xmas")
  public isXmas: boolean = false;

  @HostBinding("class.theme-xmas-snow")
  public isXmasSnow: boolean;

  constructor(
    authentication: AuthService,
    localStorageService: LocalStorageService,
    @Inject(PLATFORM_ID) platformId: object,
  ) {
    this.localStorageService = localStorageService;
    const storedDisableSnow: boolean | null = localStorageService.read(DISABLE_SNOW_KEY);
    this.isXmasSnow = storedDisableSnow !== null ? !storedDisableSnow : true;
    // Prevents SSR from rendering the banners, avoiding a hydration flash
    this.showCalendarBanner = isPlatformBrowser(platformId) && localStorageService.read(CALENDAR_BANNER_KEY) !== true;
    this.showPublicProfileBanner = isPlatformBrowser(platformId) && localStorageService.read(PUBLIC_PROFILE_BANNER_KEY) !== true;
    this.showItemsByFamilyBanner = isPlatformBrowser(platformId) && localStorageService.read(ITEMS_BY_FAMILY_BANNER_KEY) !== true;

    this.isAuthenticated$ = authentication.auth().pipe(
      rxMap((auth: AuthContext) => auth.type === ActorType.User),
    );
    this.userId$ = authentication.auth().pipe(
      rxMap((auth: AuthContext) => auth.type === ActorType.User ? auth.user.id : undefined),
    );
    this.userDisplayName$ = authentication.auth().pipe(
      rxMap((auth: AuthContext) => auth.type === ActorType.User ? auth.user.displayName : undefined),
    );
  }

  public toggleSnow(): void {
    this.isXmasSnow = !this.isXmasSnow;
    this.localStorageService.store(DISABLE_SNOW_KEY, !this.isXmasSnow);
  }

  public dismissCalendarBanner(): void {
    this.showCalendarBanner = false;
    this.localStorageService.store(CALENDAR_BANNER_KEY, true);
  }

  public dismissPublicProfileBanner(): void {
    this.showPublicProfileBanner = false;
    this.localStorageService.store(PUBLIC_PROFILE_BANNER_KEY, true);
  }

  public dismissItemsByFamilyBanner(): void {
    this.showItemsByFamilyBanner = false;
    this.localStorageService.store(ITEMS_BY_FAMILY_BANNER_KEY, true);
  }
}
